import { useCallback, useEffect, useRef, useState } from "react";

/**
 * The four view states of docs/REQUIREMENTS.md section 4, minus empty: whether
 * "no rows" is meaningful depends on the data, so each view decides that for
 * itself from a loaded value.
 */
export type Async<T> =
  { kind: "loading" } | { kind: "error"; message: string } | { kind: "ready"; value: T };

export interface AsyncResult<T> {
  state: Async<T>;
  reload: () => void;
  /** Applies a locally known change without a round trip. */
  update: (value: T) => void;
}

/**
 * One loader for every view, so the loading, error and retry behaviour cannot
 * drift between screens.
 *
 * `load` is expected to be stable, which in practice means wrapped in
 * `useCallback` or defined outside the component; an inline arrow would
 * reload on every render.
 */
export function useAsync<T>(load: () => Promise<T>, failureMessage: string): AsyncResult<T> {
  const [state, setState] = useState<Async<T>>({ kind: "loading" });

  // Two loads can be in flight after a retry, and they can finish in either
  // order. Only the newest is allowed to set state, so a slow first attempt
  // cannot overwrite the result of the retry that replaced it.
  const generation = useRef(0);

  const reload = useCallback(() => {
    const mine = (generation.current += 1);
    setState({ kind: "loading" });
    void load().then(
      (value) => {
        if (generation.current === mine) setState({ kind: "ready", value });
      },
      () => {
        if (generation.current === mine) setState({ kind: "error", message: failureMessage });
      },
    );
  }, [load, failureMessage]);

  useEffect(() => {
    reload();
    return () => {
      // Nothing that resolves after unmount may set state.
      generation.current += 1;
    };
  }, [reload]);

  const update = useCallback((value: T) => {
    setState({ kind: "ready", value });
  }, []);

  return { state, reload, update };
}
